import React, { useEffect, useState } from "react";
import { observer } from 'mobx-react-lite';
import { fabric } from 'fabric';
import { generateWrap } from "../../utility/generatewrap";
import editorStore from "../../stores/EditorStore";
import CanvasWorkspace from "./CanvasWorkspace";
import CanvasSidePanel from "./CanvasSidePanel";
import productSizeStore from "../../stores/ProductSizeStore";
import productOrientationStore from "../../stores/ProductOrientationStore";
import CanvasStore from "../../stores/CanvasStore";
import Header from "../../Components/Header";
import CanvasToolbar from "./CanvasToolbar";
import { useLocation } from "react-router-dom";
import CartModal from "../../Components/CartModal";
import CartService from "../../Services/CartService";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { canvasImageStore } from "../../stores/ImageStore";
import debounce from "lodash.debounce";
import canvasNameStore from "../../stores/CanvasNameStore";

const headerLinks = [{ name: "Home", href: "/" }];

interface CanvasCreateResponse {
  canvasID: number;
  frameType: string;
  customizeProperties: string;
  uploadID: number;
}


interface ProjectDetails {
  associatedProductID: number;
  productType: string;
  projectName: string;
  lastModified: string;
  productBasePrice: number;
  productId: number;
  width: number;
  height: number;
  projectDesc: string | null;
}

interface cartInputs {
  projectName: string;
  productBasePrice: number;
  productId: number;
  canvasId: number;
  productType: string;
  canvasName: string;
}

interface ProjectNames {
  projectName: string;
  description?: string;
}

const CanvasIndex = observer(() => {
  const [canvasCreate, setCanvasCreate] = useState<CanvasCreateResponse | null>(null);
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [cartData, setCartData] = useState<cartInputs | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [canvasName, setCanvasName] = useState("");
  const [canvasDesc, setCanvasDesc] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const canvasData = location.state?.canvasData as CanvasCreateResponse | null;
  const projectDetails = location.state?.projectDetails as ProjectDetails | null;
  const [cartItemCount, setCartItemCount] = useState(0);
  const projectNames: ProjectNames = canvasNameStore.getProjectDetails;

  useEffect(() => {
    CartService.GetCartCount()
      .then(({ CartCount }) => setCartItemCount(CartCount))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (projectNames?.projectName) setCanvasName(projectNames.projectName);
  }, [projectNames]);

  useEffect(() => {
    if (projectDetails?.projectName) setCanvasName(projectDetails.projectName);
  }, [projectDetails]);

  // Save to localStorage on page unload — tagged with canvasId so stale data is never
  // mistakenly restored for a different canvas session.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      const canvasID = canvasData?.canvasID ?? canvasCreate?.canvasID ?? null;
      if (canvasID) {
        localStorage.setItem('savedCanvas', JSON.stringify({
          canvasId: canvasID,
          json: editorStore.toJSON(),
        }));
      }
      e.returnValue = '';
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [canvasData, canvasCreate]);

  // Restore from localStorage ONLY when returning to the same canvas after a page refresh.
  // For a new canvas (no canvasId match) we clear the stale entry and start fresh.
  useEffect(() => {
    const raw = localStorage.getItem('savedCanvas');
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      const currentCanvasId = canvasData?.canvasID ?? null;
      if (currentCanvasId && saved.canvasId === currentCanvasId) {
        editorStore.loadJSON(saved.json);
      } else {
        // Different canvas session — discard the stale saved state
        localStorage.removeItem('savedCanvas');
      }
    } catch (_) {
      localStorage.removeItem('savedCanvas');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set canvas size and wrap overlay when product dimensions are known
  useEffect(() => {
    const { width, height } = productSizeStore.getSelectedProductDetails;
    const orientation = productOrientationStore.getSelectedOrientation;

    if (width > 0 && height > 0) {
      editorStore.clear();

      let finalWidth = width;
      let finalHeight = height;
      if (orientation === 'Horizontal') { finalWidth = height; finalHeight = width; }

      editorStore.setSize(finalWidth, finalHeight);

      const wrapWidth = finalWidth + 216;
      const wrapHeight = finalHeight + 216;
      editorStore.addPage().addElement({
        type: "svg",
        src: generateWrap({ width: wrapWidth, height: wrapHeight, padding: 200 }),
        width: finalWidth,
        height: finalHeight,
        selectable: false,
        alwaysOnTop: true,
        showInExport: false,
        opacity: 0.5,
      });
    }
  }, [
    productSizeStore.getSelectedProductDetails.width,
    productSizeStore.getSelectedProductDetails.height,
    productOrientationStore.getSelectedOrientation,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing canvas data when editing from My Accounts
  useEffect(() => {
    const loadCanvasData = async () => {
      if (!canvasData) return;
      const { customizeProperties } = canvasData;
      if (projectDetails) setCanvasName(projectDetails.projectName);
      if (projectDetails) {
        setCartData({
          projectName: `${projectDetails.width}x${projectDetails.height} Wrapped canvas (1.5 depth)`,
          productBasePrice: projectDetails.productBasePrice || 0,
          productId: projectDetails.productId,
          canvasId: canvasData.canvasID,
          productType: projectDetails.productType || "",
          canvasName: projectDetails.projectName,
        });
      }
      try {
        const jsonCanvas = JSON.parse(customizeProperties);
        editorStore.loadJSON(jsonCanvas);
        await editorStore.toDataURL();
      } catch (err) {
        console.error("Error loading canvas data:", err);
      }
    };
    loadCanvasData();
  }, [canvasData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create canvas record on backend for new canvases
  useEffect(() => {
    const { productId } = productSizeStore.getSelectedProductDetails;
    const orientation = productOrientationStore.getSelectedOrientation;
    const savedCanvas = localStorage.getItem("canvasData");
    const imageId = localStorage.getItem('uploadId');

    if (!savedCanvas && productId && orientation && !canvasData) {
      const json = editorStore.toJSON();
      const parsedUploadID = imageId ? parseInt(imageId, 10) : 0;
      CanvasStore.createCanvas({
        frameType: orientation,
        customizeProperties: JSON.stringify(json),
        uploadID: parsedUploadID,
        canvasProjectName: projectNames.projectName,
        canvasProjectDesc: projectNames.description,
      }, productId)
        .then(response => {
          setCanvasCreate(response);
          if (response?.canvasProjectName) {
            setCanvasName(response.canvasProjectName);
            setCanvasDesc(response.canvasProjectDesc);
          }
        })
        .catch(console.error);
    }
  }, [productSizeStore.getSelectedProductDetails.productId, productOrientationStore.getSelectedOrientation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (canvasCreate?.canvasID) {
      const { basePrice, productName, productId, productType } = productSizeStore.getSelectedProductDetails;
      setCartData({
        projectName: productName || "",
        productBasePrice: basePrice || 0,
        productId: productId || 0,
        canvasId: canvasCreate.canvasID,
        productType: productType || "",
        canvasName,
      });
    }
  }, [canvasCreate, productSizeStore.getSelectedProductDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  const base64ToBlob = (base64: string, contentType = "image/jpeg"): Blob => {
    const byteCharacters = atob(base64.split(",")[1]);
    const byteArrays: BlobPart[] = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  };

  // Auto-save on change
  useEffect(() => {
    const debouncedSave = debounce(async () => {
      const { productId } = productSizeStore.getSelectedProductDetails;
      const proId = projectDetails?.associatedProductID ?? productId;
      const frameType = canvasData?.frameType ?? productOrientationStore.getSelectedOrientation;
      const canvasID = canvasData?.canvasID ?? canvasCreate?.canvasID ?? 0;

      if (!proId || !canvasID) return;
      try {
        const json = editorStore.toJSON();
        // Safety: don't save if editorStore was cleared/destroyed
        if (!json.pages || json.pages.length === 0) return;
        const uploadedImageUrl = await editorStore.toDataURL({ pageId: editorStore.activePageId });
        const blob = base64ToBlob(uploadedImageUrl);
        const formData = new FormData();
        formData.append("files", blob, `${editorStore.activePageId}.jpeg`);
        const uploadResponse = await canvasImageStore.uploadImages(formData, false);
        await CanvasStore.updateCanvas({
          frameType,
          customizeProperties: JSON.stringify(json),
          uploadID: uploadResponse?.successFiles[0]?.id,
          canvasProjectName: canvasName,
          canvasProjectDesc: canvasDesc,
        }, proId, canvasID).then(response => {
          if (response) handleSaveSuccess();
        });
      } catch (err) {
        console.error("Error auto-saving canvas:", err);
      }
    }, 10000);

    const off = editorStore.on("change", debouncedSave);
    return () => { off(); debouncedSave.cancel(); };
  }, [canvasCreate, projectDetails, canvasData, canvasName, canvasDesc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn when image doesn't cover the inner white region (inside the "Folded edge" band) —
  // re-shown on each edit until every image covers it; always auto-closes after the default ~5s
  useEffect(() => {
    const TOAST_ID = 'image-coverage';
    const TOLERANCE = 1;

    /** Inner edge of the wrap overlay's red stroke in canvas coords, clipped to the page.
     *  Falls back to the whole page when the overlay isn't on the canvas. */
    const getInnerRegion = () => {
      let left = 0, top = 0, right = editorStore.width, bottom = editorStore.height;
      const wrap = editorStore.activePage.children.find(el =>
        el.type === 'svg' && el.showInExport === false && el.fabricObject?.type === 'group'
      )?.fabricObject as fabric.Group | undefined;
      const rect = wrap?.getObjects().find(o => o.type === 'rect');
      if (rect) {
        // calcTransformMatrix includes the group transform and maps the rect's centre-origin space to canvas coords
        const m = rect.calcTransformMatrix();
        const hw = (rect.width || 0) / 2 - (rect.strokeWidth || 0) / 2;
        const hh = (rect.height || 0) / 2 - (rect.strokeWidth || 0) / 2;
        const tl = fabric.util.transformPoint(new fabric.Point(-hw, -hh), m);
        const br = fabric.util.transformPoint(new fabric.Point(hw, hh), m);
        left = Math.max(left, Math.min(tl.x, br.x));
        top = Math.max(top, Math.min(tl.y, br.y));
        right = Math.min(right, Math.max(tl.x, br.x));
        bottom = Math.min(bottom, Math.max(tl.y, br.y));
      }
      return { left, top, right, bottom };
    };

    const checkImageFit = () => {
      const { left, top, right, bottom } = getInnerRegion();
      // Region corners, pulled in slightly so an image aligned exactly on the edge still counts as covering
      const corners = [
        new fabric.Point(left + TOLERANCE, top + TOLERANCE),
        new fabric.Point(right - TOLERANCE, top + TOLERANCE),
        new fabric.Point(right - TOLERANCE, bottom - TOLERANCE),
        new fabric.Point(left + TOLERANCE, bottom - TOLERANCE),
      ];
      const hasUncoveredImage = editorStore.activePage.children.some(el =>
        el.type === 'image' && el.showInExport !== false && el.fabricObject &&
        // The image is convex, so it covers the region iff it contains all four corners (works when rotated too)
        !corners.every(p => el.fabricObject!.containsPoint(p, undefined, true, true))
      );
      if (hasUncoveredImage && !toast.isActive(TOAST_ID)) {
        toast.warning("Image does not cover the entire canvas!", {
          toastId: TOAST_ID,
          position: "top-right",
        });
      }
    };
    const debouncedCheck = debounce(checkImageFit, 1000);
    const off = editorStore.on("change", debouncedCheck);
    return () => { off(); debouncedCheck.cancel(); toast.dismiss(TOAST_ID); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddToCart = () => {
    // Ignore the page background/border rects and editor-only overlays (wrap guide) when checking for content
    const content = editorStore.activePage.children.filter(el =>
      el.type !== 'pageBg' && el.type !== 'pageBorder' && el.showInExport !== false
    );
    if (content.length > 0) {
      setIsCartModalOpen(true);
    } else {
      toast.warning("Empty canvas can not add to cart!", { position: 'top-right' });
    }
  };

  const handleSaveSuccess = () => {
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 1000);
  };

  const handleSaveButton = async () => {
    const { productId } = productSizeStore.getSelectedProductDetails;
    const proId = projectDetails?.associatedProductID ?? productId;
    const frameType = canvasData?.frameType ?? productOrientationStore.getSelectedOrientation;
    const canvasID = canvasData?.canvasID ?? canvasCreate?.canvasID ?? 0;

    if (!proId || !canvasID) return;
    try {
      const json = editorStore.toJSON();
      const uploadedImageUrl = await editorStore.toDataURL({ pageId: editorStore.activePageId });
      const blob = base64ToBlob(uploadedImageUrl);
      const formData = new FormData();
      formData.append("files", blob, `${editorStore.activePageId}.jpeg`);
      const uploadResponse = await canvasImageStore.uploadImages(formData, false);

      const finalName = projectNames.projectName && projectNames.projectName !== canvasName
        ? `${projectNames.projectName}${canvasName}` : canvasName;
      const finalDesc = projectNames.description && projectNames.description !== canvasDesc
        ? `${projectNames.description}${canvasDesc}` : canvasDesc;

      await CanvasStore.updateCanvas({
        frameType,
        customizeProperties: JSON.stringify(json),
        uploadID: uploadResponse?.successFiles[0]?.id,
        canvasProjectName: finalName,
        canvasProjectDesc: finalDesc,
      }, proId, canvasID).then(response => {
        if (response) {
          toast.success('Canvas updated successfully', {
            position: 'top-right',
            autoClose: 2000,
            onClose: () => navigate('/myaccounts', { replace: false }),
          });
        }
      });
    } catch (err) {
      console.error("Error saving canvas:", err);
    }
  };

  return (
    <>
      <Header
        cartItemsCount={cartItemCount}
        title="Project"
        links={headerLinks}
        productName={cartData?.projectName}
        canvasName={cartData?.canvasName}
        price={cartData?.productBasePrice}
        onAddToCart={handleAddToCart}
        onShowSuccess={showSuccessMessage}
        onSaveButton={true}
        handleSaveButton={handleSaveButton}
      />
      <CanvasToolbar
        onThumbnailClick={() => {}}
        onPhotobookEditorClick={() => {}}
        showTools={false}
      />

      {/* Editor layout — mirrors Polotno's PolotnoContainer structure */}
      <div style={{ display: "flex", width: "100vw", height: "calc(100vh - 112px)", overflow: "hidden" }}>
        <CanvasSidePanel />
        <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          <CanvasWorkspace />
        </div>
      </div>

      {isCartModalOpen && cartData && (
        <CartModal cartInputs={cartData} onClose={() => setIsCartModalOpen(false)} />
      )}
    </>
  );
});

export default CanvasIndex;
